import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Send, Loader2, Sparkles, Trash2, ChevronDown, CheckCircle2, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { aiChat, aiChatHistory, aiChatAction, aiClearHistory, getAdminUser } from "@/lib/api";

// =============================================================================
// Markdown-lite renderer (bold, code, lists, headers, tables, links)
// =============================================================================
const renderRich = (text) => {
  if (!text) return null;
  // Strip out action JSON blocks — they're handled separately via .actions
  let cleaned = text.replace(/```action[\s\S]*?```/g, "").trim();

  // Convert basic markdown
  const lines = cleaned.split("\n");
  const out = [];
  let inTable = false;
  let tableRows = [];

  const flushTable = () => {
    if (tableRows.length) {
      out.push(
        <div key={`table-${out.length}`} className="my-2 overflow-x-auto">
          <table className="text-xs border-collapse">
            <tbody>
              {tableRows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="border px-2 py-1" style={{ borderColor: "#E2E8F0", color: ri === 0 ? "#0B2545" : "#475569", fontWeight: ri === 0 ? 600 : 400, background: ri === 0 ? "#F8FAFC" : "#FFFFFF" }}>
                      {inline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      tableRows = [];
    }
    inTable = false;
  };

  const inline = (s) => {
    // bold, italic, code
    const parts = [];
    const regex = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
    let lastIdx = 0;
    let m;
    while ((m = regex.exec(s)) !== null) {
      if (m.index > lastIdx) parts.push(s.slice(lastIdx, m.index));
      const tk = m[0];
      if (tk.startsWith("**")) {
        parts.push(<strong key={parts.length} style={{ color: "#0B2545" }}>{tk.slice(2, -2)}</strong>);
      } else if (tk.startsWith("`")) {
        parts.push(<code key={parts.length} className="px-1 py-0.5 rounded text-[11px]" style={{ background: "rgba(11,37,69,0.06)", color: "#0B2545", fontFamily: "ui-monospace, monospace" }}>{tk.slice(1, -1)}</code>);
      } else if (tk.startsWith("[")) {
        const lm = /\[([^\]]+)\]\(([^)]+)\)/.exec(tk);
        if (lm) parts.push(<a key={parts.length} href={lm[2]} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "#0B2545" }}>{lm[1]}</a>);
      }
      lastIdx = m.index + tk.length;
    }
    if (lastIdx < s.length) parts.push(s.slice(lastIdx));
    return <>{parts}</>;
  };

  lines.forEach((raw, i) => {
    const ln = raw.trimEnd();
    const isTableSep = /^\|?\s*[-:|\s]+\|[-:|\s]*$/.test(ln) && ln.includes("|");
    const isTableRow = ln.startsWith("|") && ln.endsWith("|") && !isTableSep;
    if (isTableRow) {
      const cells = ln.slice(1, -1).split("|").map((c) => c.trim());
      tableRows.push(cells);
      inTable = true;
      return;
    }
    if (isTableSep && inTable) {
      // skip separator line
      return;
    }
    if (inTable) flushTable();

    if (ln.startsWith("### ")) { out.push(<h4 key={i} className="font-display text-sm font-semibold mt-2" style={{ color: "#0B2545" }}>{inline(ln.slice(4))}</h4>); return; }
    if (ln.startsWith("## ")) { out.push(<h3 key={i} className="font-display text-base font-semibold mt-3" style={{ color: "#0B2545" }}>{inline(ln.slice(3))}</h3>); return; }
    if (ln.startsWith("# ")) { out.push(<h2 key={i} className="font-display text-lg font-semibold mt-3" style={{ color: "#0B2545" }}>{inline(ln.slice(2))}</h2>); return; }
    if (/^\s*[-*]\s+/.test(ln)) {
      out.push(<div key={i} className="flex gap-2 ml-2"><span style={{ color: "#94A3B8" }}>•</span><span>{inline(ln.replace(/^\s*[-*]\s+/, ""))}</span></div>);
      return;
    }
    if (/^\s*\d+\.\s+/.test(ln)) {
      out.push(<div key={i} className="flex gap-2 ml-2"><span style={{ color: "#94A3B8" }}>{ln.match(/^\s*(\d+)\./)[1]}.</span><span>{inline(ln.replace(/^\s*\d+\.\s+/, ""))}</span></div>);
      return;
    }
    if (ln === "---") { out.push(<hr key={i} className="my-2" style={{ borderColor: "#E2E8F0" }} />); return; }
    if (ln.trim() === "") { out.push(<div key={i} className="h-2" />); return; }
    out.push(<p key={i} className="leading-relaxed">{inline(ln)}</p>);
  });
  flushTable();
  return out;
};

// =============================================================================
// Action button wired to /api/ai/chat/action
// =============================================================================
const ActionChip = ({ action, onApplied, onScrollToBottom }) => {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [err, setErr] = useState(false);
  const statusRef = useRef(null);

  // After the Applied/Rejected state card mounts, prefer scrollIntoView on that
  // exact node so tall bubbles don't push the confirmation off-screen; fall
  // back to the container-scroll callback for platforms where the ref isn't
  // populated (SSR, older browsers).
  const revealStatus = useCallback(() => {
    const el = statusRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    } else {
      onScrollToBottom?.();
    }
  }, [onScrollToBottom]);

  const apply = async () => {
    setBusy(true); setErr(false);
    try {
      const res = await aiChatAction(action);
      if ((action.type === "draft_outreach_email" || action.type === "draft_email") && res?.body) {
        try { await navigator.clipboard.writeText(`Subject: ${res.subject}\nTo: ${res.to || ""}\n\n${res.body}`); } catch { /* ignore */ }
        toast.success("Email drafted & copied.", { description: res.subject });
      } else {
        toast.success(action.label || `Done · ${action.type}`);
      }
      setDone(true);
      onApplied?.(action, res);
      // Reveal the "Applied ✓" status card even when the chip is below the fold.
      // Two RAFs let React commit the state → DOM before we measure scrollHeight.
      requestAnimationFrame(() => requestAnimationFrame(revealStatus));
    } catch (e) {
      setErr(true);
      toast.error(e?.response?.data?.detail || `Action failed: ${action.type}`);
    } finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true);
    try {
      if (action.audit_id) {
        await aiChatAction({ type: "reject", audit_id: action.audit_id });
      }
      setRejected(true);
      toast("Suggestion rejected.");
      requestAnimationFrame(() => requestAnimationFrame(revealStatus));
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  if (done) return (
    <div ref={statusRef}
         className="w-full flex items-center gap-1.5 text-[11px] font-medium px-3 py-1.5 rounded-lg border"
         style={{ background: "rgba(15,109,93,0.10)", color: "#0F6D5D", borderColor: "rgba(15,109,93,0.3)" }}
         data-testid={`action-applied-${action.type}`}>
      <CheckCircle2 className="h-3 w-3" /> Applied · {action.label || action.type}
    </div>
  );
  if (rejected) return (
    <div ref={statusRef}
         className="w-full flex items-center gap-1.5 text-[11px] font-medium px-3 py-1.5 rounded-lg border"
         style={{ background: "#F8FAFC", color: "#64748B", borderColor: "#E2E8F0" }}
         data-testid={`action-rejected-${action.type}`}>
      Rejected · {action.label || action.type}
    </div>
  );

  const paramsPreview = Object.entries(action)
    .filter(([k]) => !["type", "label", "audit_id"].includes(k))
    .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : String(v)}`).join(" · ");

  return (
    <div className="w-full rounded-lg border p-2.5"
         style={{ background: err ? "rgba(155,28,28,0.05)" : "rgba(212,175,55,0.08)",
                  borderColor: err ? "rgba(155,28,28,0.3)" : "rgba(212,175,55,0.45)" }}
         data-testid={`action-card-${action.type}`}>
      <div className="flex items-start gap-2">
        <span className="grid place-items-center h-6 w-6 rounded-full shrink-0"
              style={{ background: "rgba(212,175,55,0.24)", color: "#7A5E12" }}>
          <Sparkles className="h-3 w-3" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold" style={{ color: "#0B2545" }}>
            {action.label || `Proposed: ${action.type}`}
          </div>
          {paramsPreview ? (
            <div className="text-[10px] mt-0.5 break-words" style={{ color: "#64748B" }}>
              {paramsPreview}
            </div>
          ) : null}
        </div>
      </div>
      <div className="mt-2 flex gap-1.5 justify-end">
        <button onClick={reject} disabled={busy}
                className="text-[10px] px-2 py-1 rounded-md border"
                style={{ borderColor: "#E2E8F0", background: "#FFF", color: "#475569" }}
                data-testid={`action-reject-${action.type}`}>
          Reject
        </button>
        <button onClick={apply} disabled={busy}
                className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-md font-semibold"
                style={{ background: "#0B2545", color: "#FFD774" }}
                data-testid={`action-accept-${action.type}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : err ? <AlertCircle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
          Accept
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// Quick prompt suggestions
// =============================================================================
const QUICK_PROMPTS = [
  "What new leads came in today?",
  "Which leads are awaiting payment?",
  "How much revenue did we make this month?",
  "Show me high-priority leads.",
  "What services are most requested this month?",
  "How many unpaid invoices?",
  "Who is on dispatch today?",
  "Recommend supplies for a Drug Test.",
];

// =============================================================================
// AssistantChat — reusable chat surface used in floating drawer + full page
// =============================================================================
export default function AssistantChat({ onClose, dense = false, context = null, entityLabel = null }) {
  const user = getAdminUser();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await aiChatHistory();
      setMessages(res.messages || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load chat history.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  useEffect(() => {
    scrollToBottom();
  }, [messages.length, sending, scrollToBottom]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setSending(true);
    // Optimistic user bubble
    const tempUserId = `temp-${Date.now()}`;
    setMessages((m) => [...m, { id: tempUserId, role: "user", content: msg, user_id: user?.id || "me", ts: new Date().toISOString() }]);
    try {
      const res = await aiChat(msg, context);
      setMessages((m) => {
        const without = m.filter((x) => x.id !== tempUserId);
        return [...without, res.user_message, res.assistant_message];
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || "AI assistant failed to respond.";
      toast.error(detail);
      setMessages((m) => [...m, {
        id: `err-${Date.now()}`,
        role: "assistant",
        content: `⚠️ ${detail}`,
        user_id: "ai",
        ts: new Date().toISOString(),
        _error: true,
      }]);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear the entire AI conversation? This affects all admins.")) return;
    try {
      await aiClearHistory();
      setMessages([]);
      toast.success("Chat history cleared.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear history.");
    }
  };

  const isEmpty = messages.length === 0;
  const containerPad = dense ? "p-3" : "p-4 sm:p-5";

  return (
    <div className="flex flex-col h-full bg-white" data-testid="assistant-chat">
      {/* Header */}
      <header
        className="shrink-0 px-4 sm:px-5 py-3 border-b flex items-center justify-between gap-2"
        style={{ borderColor: "#E2E8F0", background: "linear-gradient(180deg,#FFFFFF,rgba(212,175,55,0.04))" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="grid place-items-center h-8 w-8 rounded-lg shrink-0"
                style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}>
            <Sparkles className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <div className="font-display text-sm font-semibold leading-tight" style={{ color: "#0B2545" }}>
              T&amp;J Operations AI
            </div>
            <div className="text-[10px] leading-tight" style={{ color: "#64748B" }}>
              Live ops · revenue · dispatch · contracts
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {user?.id === "beckett" && messages.length > 0 ? (
            <button onClick={clearAll} className="h-8 w-8 grid place-items-center rounded hover:bg-slate-100" title="Clear conversation" data-testid="assistant-clear">
              <Trash2 className="h-3.5 w-3.5" style={{ color: "#64748B" }} />
            </button>
          ) : null}
          {onClose ? (
            <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded hover:bg-slate-100" title="Close" data-testid="assistant-close">
              <ChevronDown className="h-4 w-4" style={{ color: "#64748B" }} />
            </button>
          ) : null}
        </div>
      </header>

      {/* Body */}
      <div ref={scrollRef} className={`flex-1 min-h-0 overflow-y-auto ${containerPad} space-y-4`} data-testid="assistant-messages">
        {loading ? (
          <div className="grid place-items-center py-12">
            <Loader2 className="h-5 w-5 animate-spin" style={{ color: "#7A5E12" }} />
          </div>
        ) : isEmpty ? (
          <Empty onPrompt={(p) => send(p)} userName={user?.name || "Beckett"} />
        ) : (
          messages.map((m) => <MessageBubble key={m.id} m={m} onApplied={loadHistory} onScrollToBottom={scrollToBottom} />)
        )}
        {sending ? <TypingBubble /> : null}
        {error ? <p className="text-xs" style={{ color: "#9B1C1C" }}>{error}</p> : null}
      </div>

      {/* Input */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="shrink-0 border-t bg-white px-3 sm:px-4 py-3"
        style={{ borderColor: "#E2E8F0" }}
      >
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about leads, revenue, dispatch, supplies…"
            className="flex-1 h-10 px-3 rounded-lg border text-sm outline-none focus:border-slate-400"
            style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
            disabled={sending}
            data-testid="assistant-input"
          />
          <Button
            type="submit"
            disabled={sending || !input.trim()}
            className="h-10 px-4"
            style={{ background: "#0B2545", color: "#FFFFFF" }}
            data-testid="assistant-send"
          >
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          </Button>
        </div>
        <p className="mt-1.5 text-[10px]" style={{ color: "#94A3B8" }}>
          Powered by Claude · Shared across admins · Actions require one-click confirmation
        </p>
      </form>
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================
const Empty = ({ onPrompt, userName }) => (
  <div className="text-center py-6 sm:py-10" data-testid="assistant-empty">
    <div className="inline-grid place-items-center h-14 w-14 rounded-full mb-3"
         style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}>
      <Sparkles className="h-6 w-6" />
    </div>
    <h2 className="font-display text-lg font-semibold" style={{ color: "#0B2545" }}>
      Hi {userName} — how can I help?
    </h2>
    <p className="text-xs mt-1.5 max-w-sm mx-auto" style={{ color: "#64748B" }}>
      Ask about today&apos;s leads, revenue, dispatch status, supplies, or have me draft outreach emails.
    </p>
    <div className="mt-4 flex flex-wrap justify-center gap-1.5 max-w-md mx-auto">
      {QUICK_PROMPTS.map((p) => (
        <button
          key={p}
          onClick={() => onPrompt(p)}
          className="text-[11px] px-2.5 py-1 rounded-full border hover:bg-slate-50 transition-colors"
          style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
          data-testid={`prompt-${p.slice(0, 12)}`}
        >
          {p}
        </button>
      ))}
    </div>
  </div>
);

const MessageBubble = ({ m, onApplied, onScrollToBottom }) => {
  const isUser = m.role === "user";
  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`msg-${m.role}`}>
      <span
        className="grid place-items-center h-7 w-7 rounded-full shrink-0 text-[10px] font-semibold"
        style={{
          background: isUser ? "rgba(11,37,69,0.08)" : "rgba(212,175,55,0.18)",
          color: isUser ? "#0B2545" : "#7A5E12",
        }}
        title={m.user_id}
      >
        {isUser ? (m.user_id || "?").slice(0, 1).toUpperCase() : <Sparkles className="h-3.5 w-3.5" />}
      </span>
      <div className={`max-w-[88%] ${isUser ? "" : "flex-1"} space-y-1.5`}>
        <div
          className={`rounded-2xl px-3.5 py-2.5 text-sm ${m._error ? "" : ""}`}
          style={{
            background: isUser ? "#0B2545" : "#F8FAFC",
            color: isUser ? "#FFFFFF" : "#0B2545",
            border: isUser ? "none" : "1px solid #E2E8F0",
            borderTopRightRadius: isUser ? "4px" : undefined,
            borderTopLeftRadius: isUser ? undefined : "4px",
          }}
        >
          <div className={`prose-sm space-y-1.5 ${isUser ? "text-white" : ""}`} style={{ color: "inherit" }}>
            {renderRich(m.content)}
          </div>
        </div>
        {Array.isArray(m.actions) && m.actions.length > 0 ? (
          <div className="flex flex-col gap-1.5" data-testid="assistant-actions">
            {m.actions.map((a, i) => (
              <ActionChip key={i} action={a} onApplied={onApplied} onScrollToBottom={onScrollToBottom} />
            ))}
          </div>
        ) : null}
        <div className={`text-[10px] ${isUser ? "text-right" : ""}`} style={{ color: "#94A3B8" }}>
          {new Date(m.ts).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
          {m.user_id && m.user_id !== "ai" ? <> · {m.user_id}</> : null}
        </div>
      </div>
    </div>
  );
};

const TypingBubble = () => (
  <div className="flex gap-2.5" data-testid="assistant-typing">
    <span className="grid place-items-center h-7 w-7 rounded-full shrink-0"
          style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}>
      <Sparkles className="h-3.5 w-3.5" />
    </span>
    <div className="rounded-2xl px-3.5 py-2.5 flex items-center gap-1.5"
         style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
      <Dot delay={0} />
      <Dot delay={120} />
      <Dot delay={240} />
    </div>
  </div>
);

const Dot = ({ delay }) => (
  <span
    className="h-1.5 w-1.5 rounded-full animate-bounce"
    style={{ background: "#94A3B8", animationDelay: `${delay}ms` }}
  />
);
